import { useEffect } from 'react'
import { cx, css, useTheme } from '@fable/theme'
import BooklistDefaultIcon from '../../assets/icons/components/BooklistDefaultIcon'
import { FlexBox, Grid, TextArea } from '@fable/components'
import Field from '../field/Field'
import EyeIconCrossedOutFilled from '../../assets/icons/components/EyeIconCrossedOutFilled'
import FieldToggle from '../field_toggle/FieldToggle'
import { useTypedSelector, useActions } from '../../hooks'
import { isPrivate } from '../../utils'
import BookListThumbnail from '../book_list_thumbnail/BookListThumbnail'
import { BooklistDelete } from './BooklistDelete'

interface Props {
  mode?: 'create' | 'edit'
}

const BooklistManage: React.VFC<Props> = ({ mode = 'create' }) => {
  const { colors } = useTheme()
  const { createBookList, createBookListClear } = useActions()
  const { name, description, privacy } = useTypedSelector(
    ({ books }) => books.createdBookList
  )
  const { bookList } = useTypedSelector(({ books }) => books)
  const isEditMode = mode === 'edit'
  const isSystem = bookList.data?.type === 'system'

  // In edit mode, we need to populate the form fields with default values
  useEffect(() => {
    if (isEditMode && bookList?.data) {
      createBookList({
        name: bookList.data?.name,
        description: bookList.data?.description,
        privacy: bookList.data?.privacy,
      })
    }
  }, [isEditMode, bookList.data, createBookList])

  useEffect(
    () => () => {
      createBookListClear()
    },
    [createBookListClear]
  )

  return (
    <Grid
      className={cx(
        css`
          width: 100%;
          padding: 0 40px 20px 40px;
          background: ${colors.whiteFang};
          .field-wrap.has-label,
          input {
            margin-bottom: 0;
          }
        `,
        'booklist-create'
      )}
      gap="8px 0px"
      gridTemplateRows="194px 72px 138px auto"
    >
      <FlexBox centerAll>
        {isEditMode && bookList.data ? (
          <BookListThumbnail
            className={css`
              width: 154px;
            `}
            bookListData={bookList.data}
          />
        ) : (
          <BooklistDefaultIcon />
        )}
      </FlexBox>
      <Field
        onChange={(e) => createBookList({ name: e.target.value })}
        type="text"
        value={name}
        label="List name"
        name=""
        withCount
        maxLength="30"
        placeholder="Fantasy list"
        disabled={isEditMode && isSystem}
      />
      <TextArea
        fieldHeight="120px"
        onChangeText={(s) => createBookList({ description: s })}
        value={description}
        label="Short description"
        withCount
        maxLength={100}
        placeholder="Add more info about this list..."
        disabled={isEditMode && isSystem}
      />
      <FieldToggle
        disabled={isSystem}
        title={
          <FlexBox alignItems="center">
            <div
              className={css`
                margin-right: 5px;
              `}
            >
              Private List
            </div>
            <EyeIconCrossedOutFilled />
          </FlexBox>
        }
        description="Private lists are not visible to other Fable users on your public profile."
        onToggle={() =>
          createBookList({
            privacy: privacy === 'me' ? 'everyone' : 'me',
          })
        }
        checked={isPrivate(privacy)}
      />
      {isEditMode && !isSystem && <BooklistDelete />}
    </Grid>
  )
}

export default BooklistManage
